/**
 * Minimal path-based client router (History API) — CLAUDE.md working rule #8 "app shell/router."
 * No framework/library: three routes is small enough that a real dependency would cost more than
 * it saves, and this keeps the seam trivial to read. `app-shell.ts` owns route GUARDS (auth
 * requirements); this module only owns URL <-> Route parsing/navigation.
 */
import { parseWarehouseAddress, warehouseTabAddress, type ProjectStage, type WarehouseTab } from "../rental/desk-tabs";

export type Route =
  | { name: "landing" }
  | { name: "login" }
  | { name: "riders" }
  // The two-sided offer inbox (PR3, `docs/V2_OFFER_TECH_APPROVAL.md`) — "Offers on my riders" (this
  // account as author) + "My offers" (this account as supplier). Behind auth, same as "riders"
  // (nothing in `isPublicRoute` below lists it, so `app-shell.ts`'s default `loggedIn ? route :
  // login` guard already covers it — no special-case guard code needed).
  | { name: "offers" }
  // A rental company's own stock, the menu's Warehouse (SCRUM-475). OPEN TO A GUEST again since 28.10.2026 (SCRUM-902,
  // owner: «а) Склад открыт гостю целиком, всё хранится в браузере, серийные номера — после входа. Отправка оффера
  // по-прежнему только с аккаунтом»), reversing his 05.10.2026 decision (SCRUM-567, «warehouse должно быть под
  // авторизацией… а то боты спамить начнут авторов райдеров»). `isPublicRoute()` below is the truth; this comment is
  // not a second opinion.
  // Since 19.10.2026 it carries a row of items over its list (SCRUM-647; owner: «вообще убирай этот рентал desk из меню
  // оставь варехауз как было и всё это на странице варехауза размести»): Warehouse (the default), To answer, My shows.
  | { name: "warehouse"; tab?: WarehouseTab; stage?: ProjectStage; show?: string }
  | { name: "editor"; riderId: string | null }
  | { name: "profile" }
  | { name: "admin" }
  | { name: "forgot-password" }
  // Forced password change (SCRUM-131). Requires a session — deliberately NOT public: it is the
  // one screen a temporary-password account may see, and it is reached by being signed in, not by
  // a link.
  | { name: "change-password" }
  | { name: "legal"; doc: "privacy" | "terms" }
  // Authors of the 3D models by other people (SCRUM-989): a reading page for anyone, like the legal ones.
  | { name: "credits" }
  // The two token-carrying routes arrive from an emailed link, so they must be reachable while
  // LOGGED OUT — app-shell.ts's guard treats them as public for exactly that reason.
  | { name: "reset-password"; token: string | null }
  | { name: "verify-email"; token: string | null }
  // Confirms a pending EMAIL CHANGE (docs/V2_EMAIL_VERIFICATION.md) — a separate route from
  // "verify-email" above: it redeems a different token kind against a different API endpoint
  // (`/auth/email-change/confirm`, not `/auth/verify-email/confirm`) and the link is emailed to the
  // NEW address, not the account's current one. Same "reachable while logged out" reasoning either
  // way — the browser that clicks it may not carry the account's session at all.
  | { name: "verify-email-change"; token: string | null }
  // Sign-in by the one-time link mailed to a guest who sent an offer with only an email (SCRUM-591).
  // Public for the same reason as the routes above: the browser that opens it has no session yet.
  | { name: "email-login"; token: string | null }
  // The public read-only rider viewer (`/r/:id` or the version-pinned `/r/:id/v/:version`) — v1's
  // `pub.php`/`pub.js` equivalent. Reachable with NO session, same reasoning as the recovery routes
  // above: this is the link printed as a QR code on a rider (owner ask, 2026-07-21), so it must
  // work for someone who has never logged in at all, on their phone. `/r/:id` (a short PATH, not a
  // query string) was chosen over `/view?rider=<id>` specifically because it is what gets
  // printed/QR-encoded — every character costs QR density, and a clean path reads better on a
  // business card / rider footer than a query string does.
  //
  // `version: null` -> the LATEST live publication (`GET /riders/public/:id`); a concrete version
  // -> that exact frozen snapshot (`GET /riders/public/:id/v/:version`, owner 2026-07-21 versioned-
  // publication redesign: "так и печатный экземпляр, и экран никогда не разойдутся" — this is what
  // a printed QR code should encode).
  //
  // `offerId` (`?offer=<uuid>`, PR2, `docs/V2_OFFER_TECH_APPROVAL.md`): present -> the page hands
  // the whole screen to `pages/offer-overlay.ts` instead of the normal read-only viewer. Absent
  // (the normal case) on every route this app built before PR2 — kept OPTIONAL rather than
  // `string | null` so every existing `{ name: "public-viewer", riderId, version }` literal
  // elsewhere in this codebase (riders-list-page.ts, editor-page.ts, topbar.ts, print-rider.ts,
  // public-print.ts) keeps compiling unchanged; none of them ever mean to open an offer.
  | { name: "public-viewer"; riderId: string | null; version: number | null; offerId?: string; showToken?: string }
  // "Which build is this?" (SCRUM-289). A PATH, not a modal-only affordance, so the answer can be
  // sent to someone: "open /about and tell me what it says" is a complete instruction, where "open
  // the menu, then…" is not. Public, because the question is asked most often by whoever is looking
  // at a broken-seeming dev deploy, and requiring a session to answer it would be perverse.
  | { name: "about" }
  // The page a show link leads to (SCRUM-587 decision 32, «Ссылка ведет на страницу архивного шоу»): the
  // public summary of ONE show — open (a button to make an offer) or archived (read-only, with «Repeat this
  // show» for its owner and band). Public for the same reason as the viewer: the link is for suppliers.
  | { name: "show-page"; token: string }
  // The crew task link (SCRUM-760/762): a rental company sends it to a worker, who may have no account yet.
  // Public for the same reason as the show link; the page itself decides what the visitor may see.
  | { name: "crew-task"; token: string }
  // The cabinet's Shows section (SCRUM-587 S4): Inbox and Calendar tabs, and one show's page for its owner or a
  // party (`/shows/<id>`). Behind auth like the rest of the cabinet. NOT `/show/<token>` above — that is the
  // public link; the plural is the signed-in person's own.
  | { name: "shows"; tab: "list" | "inbox" | "calendar" | "availability" }
  | { name: "show-detail"; showId: string }
  // The card a printed warehouse label opens (`/warehouse/place/<id>`, warehouse addressing 29.10.2026): the signed-in owner's own place;
  // a stranger's is a 404 on the server and the page says so. Behind sign-in like the rest of the cabinet.
  | { name: "place-card"; placeId: string }
  // One contact's calendar for the rental (SCRUM-946): a VIEW of the Crew tab with its own address, opened from the contact's row. Behind sign-in.
  | { name: "crew-person"; contactId: string };

/** `/` serves the public marketing landing page in BOTH auth states (it is `isPublicRoute`); the
 *  page itself swaps its CTA for a logged-in visitor. Everything else is an explicit path, and any
 *  unknown path falls back to `/` — a mistyped URL lands on the product pitch, never a dead end.
 *  (It used to resolve to login-or-riders depending on auth state; that stopped being true when the
 *  landing page landed, and three smoke tests kept asserting it until 2026-07-22.) */
export function parseRoute(pathname: string, search: URLSearchParams): Route {
  if (pathname === "/" ) return { name: "landing" };
  if (pathname === "/login") return { name: "login" };
  if (pathname === "/riders") return { name: "riders" };
  if (pathname === "/offers") return { name: "offers" };
  if (pathname === "/shows") {
    const tab = search.get("tab");
    return { name: "shows", tab: tab === "calendar" ? "calendar" : tab === "inbox" ? "inbox" : tab === "availability" ? "availability" : "list" };
  }
  if (pathname.startsWith("/shows/") && pathname.length > "/shows/".length) {
    return { name: "show-detail", showId: decodeURIComponent(pathname.slice("/shows/".length)) };
  }
  if (pathname.startsWith("/warehouse/place/") && pathname.length > "/warehouse/place/".length) {
    return { name: "place-card", placeId: decodeURIComponent(pathname.slice("/warehouse/place/".length)) };
  }
  // "/warehouse/crew/person?contact=<id>" is one contact's calendar (SCRUM-946); no contact named - the Crew tab itself.
  if (pathname.replace(/\/+$/, "") === "/warehouse/crew/person") {
    const contact = search.get("contact");
    return contact ? { name: "crew-person", contactId: contact } : { name: "warehouse", tab: "crew" };
  }
  // "/my-equipment" was its first address; old links still open it.
  // "/rental" was the Rental desk's address for one day (19.10.2026) and links to it were sent; it is an alias now.
  // "/warehouse/organize" is the Warehouse page with its «Organize warehouse» view open (owner, 29.10.2026: «отдельный вид внутри
  // Warehouse»); the page reads the rest of the address itself (`pages/warehouse-organize-route.ts`).
  // "/warehouse/catalogue" is the Warehouse page with «Tick from the catalogue» open as a view (owner, 30.10.2026); the page reads it itself
  // (`pages/warehouse-first-route.ts`).
  // "/warehouse/order" is My shows with the order-of-work editor open (SCRUM-939); the panel reads the rest itself (`rental/order-route.ts`).
  if (pathname.replace(/\/+$/, "") === "/warehouse/order") return { name: "warehouse", tab: "shows" };
  if (pathname === "/warehouse" || pathname === "/warehouse/organize" || pathname === "/warehouse/catalogue" || pathname === "/rental" || pathname === "/my-equipment") {
    const { tab, ...target } = parseWarehouseAddress(search);
    return tab === "warehouse" ? { name: "warehouse" } : { name: "warehouse", tab, ...target };
  }
  if (pathname === "/editor") return { name: "editor", riderId: search.get("rider") };
  if (pathname === "/profile") return { name: "profile" };
  if (pathname === "/admin") return { name: "admin" };
  if (pathname === "/forgot-password") return { name: "forgot-password" };
  if (pathname === "/change-password") return { name: "change-password" };
  if (pathname === "/about") return { name: "about" };
  if (pathname === "/privacy") return { name: "legal", doc: "privacy" };
  if (pathname === "/terms") return { name: "legal", doc: "terms" };
  if (pathname === "/credits") return { name: "credits" };
  if (pathname === "/reset-password") return { name: "reset-password", token: search.get("token") };
  if (pathname === "/verify-email") return { name: "verify-email", token: search.get("token") };
  if (pathname === "/verify-email-change") return { name: "verify-email-change", token: search.get("token") };
  if (pathname.startsWith("/show/") && pathname.length > "/show/".length) {
    return { name: "show-page", token: decodeURIComponent(pathname.slice("/show/".length)) };
  }
  if (pathname.startsWith("/crew/") && pathname.length > "/crew/".length) {
    return { name: "crew-task", token: decodeURIComponent(pathname.slice("/crew/".length)) };
  }
  if (pathname === "/email-login") return { name: "email-login", token: search.get("token") };
  // Dynamic segments (the only ones in this router): "/r/<uuid>" or "/r/<uuid>/v/<version>". An
  // empty/missing id (bare "/r" or "/r/") still routes here rather than falling through to "unknown
  // path" — the page itself shows a clear "no rider specified" message instead of a confusing
  // bounce to the marketing page. A malformed/non-numeric version segment is treated the same as
  // "no version" (falls back to "latest") rather than a hard parse error.
  if (pathname === "/r" || pathname.startsWith("/r/")) {
    const rest = pathname === "/r" ? "" : pathname.slice("/r/".length);
    const versionMatch = rest.match(/^([^/]+)\/v\/(\d+)$/);
    const offerId = search.get("offer");
    // `?show=<token>` (SCRUM-587 decision 24): the link a band gives suppliers for ONE show. It rides along the
    // whole offer flow so the offer is sent for that show (`offer/show-link.ts`).
    const showToken = search.get("show");
    const offerPart = { ...(offerId ? { offerId } : {}), ...(showToken ? { showToken } : {}) };
    if (versionMatch) {
      return { name: "public-viewer", riderId: versionMatch[1]!, version: Number(versionMatch[2]), ...offerPart };
    }
    return { name: "public-viewer", riderId: rest || null, version: null, ...offerPart };
  }
  return { name: "landing" }; // unknown path -> the public marketing page, never a dead end
}

/**
 * Routes reachable without a session. Everything else redirects to /login (app-shell.ts).
 *
 * `warehouse` is here again since 28.10.2026 (SCRUM-902, owner: «а) Склад открыт гостю целиком, всё хранится в браузере,
 * серийные номера — после входа. Отправка оффера по-прежнему только с аккаунтом»). It was taken out on 05.10.2026
 * (SCRUM-567, «warehouse должно быть под авторизацией… а то боты спамить начнут авторов райдеров»); the guard against bots
 * stays on SENDING an offer, below. A guest's warehouse lives in their browser and moves into the account when they
 * register (27.10.2026). The same owner, the same day: «мы не должны гостю дать возможность скачать каталог» — «весь»
 * (запрет 24): what the guest page may ask the server for is SCRUM-902 point 2.
 *
 * `public-viewer` stays, and that is the whole point of the split: a rental company arrives by a
 * rider link, reads it and fills an offer with no account at all (SCRUM-462). The account is asked
 * for only when the offer is SENT — the server already enforces that (`offers.controller.ts`'s
 * `POST /offers/:id/submit` sits behind `JwtAuthGuard` + `VerifiedEmailGuard`).
 */
export function isPublicRoute(route: Route): boolean {
  return (
    route.name === "landing" ||
    route.name === "login" ||
    route.name === "forgot-password" ||
    // Legal pages must be readable BEFORE you have an account — the registration notice links to them.
    route.name === "legal" ||
    route.name === "credits" ||
    route.name === "reset-password" ||
    route.name === "verify-email" ||
    route.name === "verify-email-change" ||
    route.name === "email-login" ||
    route.name === "public-viewer" ||
    route.name === "show-page" ||
    route.name === "crew-task" ||
    route.name === "warehouse" ||
    route.name === "about"
  );
}

/**
 * Where a signed-in person is asked to accept the terms (the consent gate in `app-shell.ts`, docs/V2_POST_CUTOVER_TAILS.md #7):
 * every place of the service, and none of the pages that must be readable without an account. The warehouse is open to a
 * guest since 28.10.2026 (SCRUM-902) and is still a place of the service — a rental company lands there after signing in
 * (SCRUM-647), so «not a public route» alone would never ask it.
 */
export function asksForTerms(route: Route): boolean {
  return !isPublicRoute(route) || route.name === "warehouse";
}

export function currentRoute(): Route {
  return parseRoute(window.location.pathname, new URLSearchParams(window.location.search));
}

export function routePath(route: Route): string {
  switch (route.name) {
    case "landing":
      return "/";
    case "login":
      return "/login";
    case "riders":
      return "/riders";
    case "offers":
      return "/offers";
    case "shows":
      return route.tab === "calendar" ? "/shows?tab=calendar" : route.tab === "inbox" ? "/shows?tab=inbox" : route.tab === "availability" ? "/shows?tab=availability" : "/shows";
    case "show-detail":
      return `/shows/${encodeURIComponent(route.showId)}`;
    case "place-card":
      return `/warehouse/place/${encodeURIComponent(route.placeId)}`;
    case "crew-person":
      return `/warehouse/crew/person?contact=${encodeURIComponent(route.contactId)}`;
    case "warehouse":
      return warehouseTabAddress(route.tab ?? "warehouse", null, { ...(route.stage ? { stage: route.stage } : {}), ...(route.show ? { show: route.show } : {}) });
    case "editor":
      return route.riderId ? `/editor?rider=${encodeURIComponent(route.riderId)}` : "/editor";
    case "profile":
      return "/profile";
    case "admin":
      return "/admin";
    case "forgot-password":
      return "/forgot-password";
    case "change-password":
      return "/change-password";
    case "legal":
      return route.doc === "privacy" ? "/privacy" : "/terms";
    case "reset-password":
      return route.token ? `/reset-password?token=${encodeURIComponent(route.token)}` : "/reset-password";
    case "verify-email":
      return route.token ? `/verify-email?token=${encodeURIComponent(route.token)}` : "/verify-email";
    case "verify-email-change":
      return route.token
        ? `/verify-email-change?token=${encodeURIComponent(route.token)}`
        : "/verify-email-change";
    case "email-login":
      return route.token ? `/email-login?token=${encodeURIComponent(route.token)}` : "/email-login";
    case "about":
      return "/about";
    case "credits":
      return "/credits";
    case "show-page":
      return `/show/${encodeURIComponent(route.token)}`;
    case "crew-task":
      return `/crew/${encodeURIComponent(route.token)}`;
    case "public-viewer": {
      if (!route.riderId) return "/r";
      const base =
        route.version === null
          ? `/r/${encodeURIComponent(route.riderId)}`
          : `/r/${encodeURIComponent(route.riderId)}/v/${route.version}`;
      const query = [
        ...(route.offerId ? [`offer=${encodeURIComponent(route.offerId)}`] : []),
        ...(route.showToken ? [`show=${encodeURIComponent(route.showToken)}`] : []),
      ].join("&");
      return query ? `${base}?${query}` : base;
    }
  }
}

/**
 * Changes the URL and notifies listeners — `pushState`/`replaceState` alone does NOT fire
 * `popstate`, so this dispatches a synthetic one to keep a single code path (`onRouteChange`) for
 * both browser-back/forward AND in-app navigation.
 *
 * `options.replace` (default `false`, i.e. `pushState`) exists for exactly one caller:
 * `app-shell.ts`'s `resolveAndMount`, which calls this to REWRITE the URL when the route guard
 * decides the requested route isn't the one actually shown (e.g. `/editor?rider=<id>` while signed
 * out becomes `/login`). That call is a correction of the CURRENT entry, not a new navigation — using
 * `pushState` there was SCRUM-250 critic finding 4: it added a second history entry for a page that
 * was never rendered, so `resolveAndMount` runs again from `popstate` (this function's own
 * `dispatchEvent` below, AND the browser's real Back button) and pushes `/login` right back over
 * whatever Back had just popped to, trapping Back on `/login` forever with `history.length` frozen.
 * `replaceState` overwrites that same entry instead of adding one, so Back has something real to
 * land on. Every OTHER caller (a link click, a redirect after login/save, etc.) is genuine navigation
 * and keeps the default `pushState` — collapsing those into `replaceState` too would break ordinary
 * Back/Forward for everyone.
 */
export function navigate(route: Route, options?: { replace?: boolean }): void {
  const path = routePath(route);
  if (window.location.pathname + window.location.search !== path) {
    if (options?.replace) {
      window.history.replaceState({}, "", path);
    } else {
      window.history.pushState({}, "", path);
    }
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/** Fires immediately with the current route, then on every back/forward/navigate(). Returns an unsubscribe fn. */
export function onRouteChange(listener: (route: Route) => void): () => void {
  const handler = (): void => listener(currentRoute());
  window.addEventListener("popstate", handler);
  handler();
  return () => window.removeEventListener("popstate", handler);
}
